"use client"

import { useTranslations } from "next-intl"
import { X } from "lucide-react"
import { ChatReplyQuote } from "@/components/chat/ChatReplyQuote"
import type { ChatReplyPreview, ReplySenderContext } from "@/lib/chat/reply-preview"
import { buildReplyPreviewLabel, isReplyToSelf } from "@/lib/chat/reply-preview"

export type ChatComposerReplyStripProps = {
  replyTo: ChatReplyPreview
  onDismiss: () => void
  fallbackSenderName?: string
  senderContext?: ReplySenderContext
}

export function ChatComposerReplyStrip({
  replyTo,
  onDismiss,
  fallbackSenderName = "User",
  senderContext,
}: ChatComposerReplyStripProps) {
  const tChat = useTranslations("chat")
  const { senderLabel } = buildReplyPreviewLabel(replyTo, fallbackSenderName, senderContext)
  const selfReply = isReplyToSelf(replyTo, senderContext)

  return (
    <div className="relative isolate z-10 mb-2 flex items-center gap-2 rounded-md bg-background px-1 py-1">
      <div className="min-w-0 flex-1 rounded-md bg-black/[0.04] px-3 py-2 ring-1 ring-black/[0.06]">
        <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-neutral-600 dark:text-muted-foreground">
          {selfReply ? (
            <span className="normal-case text-neutral-800 dark:text-foreground/90">{tChat("replyingToSelf")}</span>
          ) : (
            <>
              {tChat("replyingTo")}{" "}
              <span className="normal-case text-neutral-800 dark:text-foreground/90">{senderLabel}</span>
            </>
          )}
        </p>
        <ChatReplyQuote
          replyTo={replyTo}
          isOwnMessage={false}
          compact
          showSender={false}
          fallbackSenderName={fallbackSenderName}
          senderContext={senderContext}
        />
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full hover:bg-accent"
        aria-label={tChat("cancelReply")}
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  )
}
